import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  Keyboard,
  LockKeyhole,
  RotateCcw,
  X
} from 'lucide-react';
import './RecipeLibraryReview.css';

const STAGES = [
  { key: 'clean', short: 'Clean', title: 'What we cleaned', count: 0 },
  { key: 'units', short: 'Units', title: 'Units', count: 3 },
  { key: 'ingredients', short: 'Ingred', title: 'Ingredients', count: 14 },
  { key: 'preparations', short: 'Preps', title: 'Preparations', count: 5 },
  { key: 'dishes', short: 'Dishes', title: 'Dishes', count: 9 },
  { key: 'structure', short: 'Struct', title: 'Structure', count: 3 },
  { key: 'seal', short: 'Seal', title: 'Seal book', count: 0 },
];

const STAGE_COPY = {
  clean: { eyebrow: 'Import summary', title: 'What we cleaned', description: 'A quick record of the safe changes already made.' },
  units: { eyebrow: 'Stage 2 of 7', title: 'Set unclear units', description: 'Answer one practical measurement question at a time.' },
  ingredients: { eyebrow: 'Stage 3 of 7', title: 'Compare ingredient pairs', description: 'Decide whether each pair means the same ingredient.' },
  preparations: { eyebrow: 'Stage 4 of 7', title: 'Compare preparations', description: 'Compare recipes side by side, including their small differences.' },
  dishes: { eyebrow: 'Stage 5 of 7', title: 'Compare dishes', description: 'Resolve likely spelling matches and menu-name collisions.' },
  structure: { eyebrow: 'Stage 6 of 7', title: 'Fix book structure', description: 'Resolve issues that affect how dishes and preparations connect.' },
  seal: { eyebrow: 'Final stage', title: 'Seal Bean There', description: 'Review the result, then make this version permanent.' },
};

function Button({ className = '', children, ...props }) {
  return (
    <button className={`rlr-button ${className}`} {...props}>
      {children}
    </button>
  );
}

function PrimaryButton({ className = '', ...props }) {
  return <Button className={`rlr-button-primary ${className}`} {...props} />;
}

function StageStepper({ current, completed, onSelect }) {
  const furthestOpen = Math.min(6, Math.max(current, ...Array.from(completed), 0) + 1);

  return (
    <nav aria-label="Book review stages" className="rlr-stepper">
      <ol className="rlr-stepper-grid">
        {STAGES.map((stage, index) => {
          const isCurrent = index === current;
          const isDone = completed.has(index);
          const isLocked = index > furthestOpen;
          return (
            <li key={stage.key} className="rlr-step-li">
              {index < STAGES.length - 1 && (
                <span className={`rlr-step-line ${isDone ? 'done' : 'pending'}`} aria-hidden="true" />
              )}
              <button
                type="button"
                onClick={() => onSelect(index)}
                disabled={isLocked}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={`${stage.title}${isLocked ? ', locked' : isDone ? ', completed' : ''}`}
                className="rlr-step-btn"
              >
                <span className={`rlr-circle ${isCurrent ? 'current' : isDone ? 'done' : isLocked ? 'locked' : 'normal'}`}>
                  {isDone ? <Check style={{ width: 14, height: 14 }} strokeWidth={3} /> : isLocked ? <LockKeyhole style={{ width: 12, height: 12 }} /> : index + 1}
                </span>
                <span className={`rlr-step-label ${isCurrent ? 'current' : 'muted'}`}>{stage.short}</span>
                <span className={`rlr-step-sub ${isCurrent ? 'current' : 'muted'}`}>
                  {isDone ? 'Done' : isCurrent ? (stage.count > 0 ? `${stage.count} left` : 'Here') : isLocked ? 'Locked' : stage.count > 0 ? stage.count : 'Ready'}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function CleanStage({ openDrawer }) {
  const rows = [
    { label: 'matched existing library', value: 287, width: '84%', tone: 'bg-success' },
    { label: 'brand names removed', value: 41, width: '12%', tone: 'bg-halo' },
    { label: 'units converted', value: 19, width: '6%', tone: 'bg-warning' },
    { label: 'added as new', value: 12, width: '4%', tone: 'bg-ink-45' },
  ];
  return (
    <div className="rlr-clean-container rlr-animate-stage">
      <div className="rlr-hero-num">
        <p>340</p>
        <p>ingredient rows came in</p>
      </div>
      <div className="rlr-rows-box">
        {rows.map((row) => (
          <div key={row.label} className="rlr-row-item">
            <p className="rlr-row-lbl">{row.label}</p>
            <p className="rlr-row-val">{row.value}</p>
            <div className="rlr-bar-track">
              <div className={`rlr-bar-fill ${row.tone}`} style={{ width: row.width }} />
            </div>
          </div>
        ))}
      </div>
      <section className="rlr-examples-sec" aria-labelledby="clean-examples">
        <div className="rlr-examples-top">
          <h3 id="clean-examples" className="rlr-h3-examples">Examples</h3>
          <Button onClick={() => openDrawer('clean-list')} style={{ minHeight: 32, border: 'none', background: 'transparent', padding: '0 6px', fontSize: 12, color: 'rgba(30, 41, 59, 0.55)' }}>
            See all 359 <ChevronRight style={{ width: 14, height: 14 }} />
          </Button>
        </div>
        <div className="rlr-examples-card">
          {[
            ['TATA Salt 1kg', 'Salt'],
            ['Sugar 0.5 Kg', 'Sugar 500 g'],
            ['Eggs 1 Dozen', 'Egg 12 piece'],
          ].map(([before, after]) => (
            <div key={before} className="rlr-example-row">
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'rgba(30, 41, 59, 0.55)', textDecoration: 'line-through' }}>{before}</span>
              <ArrowRight style={{ width: 16, height: 16, flexShrink: 0, color: '#057a55' }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, color: '#1e293b' }}>{after}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function DecisionCard({ title, badge, description, note, actions, danger, warning, onDecision }) {
  const [choice, setChoice] = useState(null);
  if (choice) {
    return (
      <article className="rlr-animate-collapse" style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0,1fr) auto', alignItems: 'center', gap: 12, borderRadius: 8, border: '1px solid rgba(5,122,85,0.25)', backgroundColor: '#e6f6f0', padding: '12px 16px', marginBottom: 12 }}>
        <CheckCircle2 style={{ width: 20, height: 20, flexShrink: 0, color: '#057a55' }} />
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</p>
          <p style={{ margin: 0, fontSize: 12, color: 'rgba(30, 41, 59, 0.55)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{choice}</p>
        </div>
        <button type="button" onClick={() => setChoice(null)} style={{ border: 'none', background: 'transparent', padding: 8, cursor: 'pointer', borderRadius: 6, color: 'rgba(30, 41, 59, 0.45)' }} aria-label={`Undo decision for ${title}`} title="Undo">
          <RotateCcw style={{ width: 16, height: 16 }} />
        </button>
      </article>
    );
  }
  return (
    <article className={`rlr-card ${danger ? 'danger' : warning ? 'warning' : ''}`}>
      <div className="rlr-card-head">
        <h3 className="rlr-h3-card">{title}</h3>
        <span className="rlr-badge">{badge}</span>
      </div>
      <div className="rlr-card-desc">{description}</div>
      <div className="rlr-actions-flex">
        {actions.map((action, index) => (
          <Button
            key={action}
            onClick={() => { setChoice(action); onDecision?.(); }}
            style={index === 0 ? { borderColor: 'rgba(5, 122, 85, 0.3)', backgroundColor: '#e6f6f0', color: '#057a55' } : {}}
          >
            {index === 0 && <Check style={{ width: 16, height: 16 }} />} {action}
          </Button>
        ))}
      </div>
      {note && (
        <p style={{ marginTop: 12, display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, lineHeight: '1.25rem', color: '#057a55', margin: '12px 0 0 0' }}>
          <ChevronRight style={{ width: 14, height: 14, flexShrink: 0, marginTop: 2 }} />{note}
        </p>
      )}
    </article>
  );
}

function UnitsStage({ onDecision }) {
  return (
    <div style={{ maxWidth: '48rem', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }} className="rlr-animate-stage">
      <DecisionCard title="Coriander — 1 bunch" badge="11 dishes" description={<>“Bunch” is not a standard unit. How much does one weigh?</>} actions={['30 g', '50 g', '100 g', 'Enter grams', 'Drop']} onDecision={onDecision} />
      <DecisionCard title="Cooking Oil — 2 Tbsp" badge="23 dishes" description={<>A spoon of oil is 15 ml; a spoon of salt is 18 g. The answer depends on the ingredient.</>} actions={['Set oil density → 30 ml', 'Enter ml', 'Drop']} note="Fixes every tablespoon of oil in every future book." onDecision={onDecision} />
      <DecisionCard title="Mint — ½ handful" badge="6 dishes" description={<>“Handful” changes from person to person. Choose the kitchen standard for mint.</>} actions={['15 g', '20 g', 'Enter grams', 'Drop']} onDecision={onDecision} />
    </div>
  );
}

function PairCard({ confidence, position, leftTitle, leftMeta, rightTitle, rightMeta, evidence, actions, flag, recipes, onDecision }) {
  const [choice, setChoice] = useState(null);
  if (choice) {
    return <DecisionCard title={`${leftTitle} ↔ ${rightTitle}`} badge="Decided" description={choice} actions={['Change decision']} onDecision={() => setChoice(null)} />;
  }
  const dots = confidence === 'Very likely' ? 4 : 3;
  return (
    <article className="rlr-card">
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', alignItems: 'center', gap: 12 }}>
        <div style={{ display: 'flex', minWidth: 0, alignItems: 'center', gap: 8 }}>
          <span style={{ display: 'flex', gap: 4 }} aria-hidden="true">
            {[0, 1, 2, 3].map((dot) => (
              <span key={dot} style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: dot < dots ? '#057a55' : '#e2e8f0' }} />
            ))}
          </span>
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#057a55' }}>{confidence}</span>
          {flag && <span style={{ fontSize: 10, fontWeight: 600, color: '#c27803' }}>{flag}</span>}
        </div>
        <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 12, fontWeight: 700, color: 'rgba(30, 41, 59, 0.45)' }}>{position}</span>
      </div>

      <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto minmax(0,1fr)', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: 0, fontFamily: 'Sora, sans-serif', fontSize: 18, fontWeight: 700, color: '#1e293b', wordBreak: 'break-word' }}>{leftTitle}</h3>
          <div style={{ marginTop: 8, borderTop: '1px solid #e2e8f0', paddingTop: 8, fontSize: 12, fontWeight: 500, lineHeight: '1.25rem', color: 'rgba(30, 41, 59, 0.5)' }}>{leftMeta}</div>
        </div>
        <span style={{ marginTop: 4, fontSize: 18, fontWeight: 600, color: 'rgba(30, 41, 59, 0.25)' }}>↔</span>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: 0, fontFamily: 'Sora, sans-serif', fontSize: 18, fontWeight: 700, color: '#1e293b', wordBreak: 'break-word' }}>{rightTitle}</h3>
          <div style={{ marginTop: 8, borderTop: '1px solid #e2e8f0', paddingTop: 8, fontSize: 12, fontWeight: 500, lineHeight: '1.25rem', color: 'rgba(30, 41, 59, 0.5)' }}>{rightMeta}</div>
        </div>
      </div>

      {recipes && (
        <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, borderRadius: 8, backgroundColor: '#f8fafc', padding: 12, fontSize: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{recipes.left.map((item) => <p key={item} style={{ margin: 0, color: 'rgba(30, 41, 59, 0.65)' }}>{item}</p>)}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{recipes.right.map((item) => <p key={item} style={{ margin: 0, color: 'rgba(30, 41, 59, 0.65)' }}>{item}</p>)}</div>
        </div>
      )}

      <div style={{ marginTop: 20, borderRadius: 8, backgroundColor: '#e6f6f0', padding: 16 }}>
        <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#1e293b' }}>Why we think they’re the same</p>
        <ul style={{ marginTop: 8, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {evidence.map((item) => (
            <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, lineHeight: '1.25rem', color: 'rgba(30, 41, 59, 0.65)' }}>
              <Check style={{ width: 14, height: 14, flexShrink: 0, marginTop: 2, color: '#057a55' }} />{item}
            </li>
          ))}
        </ul>
      </div>

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {actions.map((action, index) => (
          <Button
            key={action}
            onClick={() => { setChoice(action); onDecision?.(); }}
            style={{ justifyContent: 'flex-start' }}
            className={index === 0 ? 'rlr-button-primary' : ''}
          >
            {index === 0 ? <Check style={{ width: 16, height: 16 }} /> : index === actions.length - 1 ? <X style={{ width: 16, height: 16 }} /> : null}{action}
          </Button>
        ))}
      </div>
    </article>
  );
}

function IngredientsStage({ reopenWarning, openDrawer, onDecision }) {
  const [focusMode, setFocusMode] = useState(false);
  if (focusMode) {
    return (
      <div style={{ maxWidth: '48rem', margin: '0 auto' }} className="rlr-animate-stage">
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', alignItems: 'center', gap: 12, borderBottom: '1px solid #e2e8f0', paddingBottom: 20 }}>
          <div>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: 'rgba(30, 41, 59, 0.4)' }}>Focus mode</p>
            <p style={{ margin: '4px 0 0 0', fontFamily: 'Sora, sans-serif', fontSize: 14, fontWeight: 700, color: '#1e293b' }}>Pair 3 of 14</p>
          </div>
          <Button onClick={() => setFocusMode(false)}><X style={{ width: 16, height: 16 }} /> Exit</Button>
        </div>
        <div style={{ padding: '3rem 0', textAlign: 'center' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto minmax(0,1fr)', alignItems: 'center', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <h3 style={{ margin: 0, fontFamily: 'Sora, sans-serif', fontSize: 24, fontWeight: 700, color: '#1e293b' }}>Kashmiri Mirchi</h3>
              <p style={{ margin: '8px 0 0 0', fontSize: 14, color: 'rgba(30, 41, 59, 0.5)' }}>9 dishes</p>
            </div>
            <span style={{ fontSize: 24, color: 'rgba(30, 41, 59, 0.25)' }}>↔</span>
            <div style={{ minWidth: 0 }}>
              <h3 style={{ margin: 0, fontFamily: 'Sora, sans-serif', fontSize: 24, fontWeight: 700, color: '#1e293b' }}>Kashmiri Chilli</h3>
              <p style={{ margin: '8px 0 0 0', fontSize: 14, color: 'rgba(30, 41, 59, 0.5)' }}>4 books</p>
            </div>
          </div>
          <ul style={{ margin: '2rem auto 0 auto', maxWidth: '24rem', padding: 0, listStyle: 'none', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {['Shares the word “Kashmiri”', 'mirchi = chilli', 'Never used together'].map((item) => (
              <li key={item} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'rgba(30, 41, 59, 0.65)' }}>
                <Check style={{ width: 16, height: 16, color: '#057a55' }} />{item}
              </li>
            ))}
          </ul>
          <div style={{ marginTop: 32, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
            <PrimaryButton onClick={onDecision}><span style={{ borderRadius: 4, border: '1px solid rgba(255,255,255,0.5)', padding: '2px 6px', fontSize: 12 }}>S</span> Same</PrimaryButton>
            <Button onClick={onDecision}><span style={{ borderRadius: 4, border: '1px solid #e2e8f0', padding: '2px 6px', fontSize: 12 }}>D</span> Different</Button>
            <Button>Skip</Button>
          </div>
          <p style={{ marginTop: 16, fontSize: 12, color: 'rgba(30, 41, 59, 0.4)' }}>Press S or D · auto-advances</p>
        </div>
      </div>
    );
  }
  return (
    <div style={{ maxWidth: '56rem', margin: '0 auto' }} className="rlr-animate-stage">
      {reopenWarning && (
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12, borderRadius: 8, border: '1px solid rgba(194,120,3,0.3)', backgroundColor: '#fef7e6', padding: 16, fontSize: 14, lineHeight: '1.5rem', color: 'rgba(30, 41, 59, 0.7)' }}>
          <CircleAlert style={{ width: 20, height: 20, flexShrink: 0, marginTop: 2, color: '#c27803' }} />
          <p style={{ margin: 0 }}><strong style={{ fontWeight: 700, color: '#1e293b' }}>Earlier-stage change:</strong> changing a match here will recompute Preparations and Dishes. Two later decisions may be affected.</p>
        </div>
      )}
      <div style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', gap: 16, borderRadius: 8, border: '1px solid #e2e8f0', backgroundColor: '#ffffff', padding: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontFamily: 'Sora, sans-serif', fontSize: 16, fontWeight: 700, color: '#1e293b' }}>14 pairs might be duplicates</h3>
          <p style={{ margin: '4px 0 0 0', fontSize: 12, color: 'rgba(30, 41, 59, 0.5)' }}>6 very likely · 5 likely · 3 possible</p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <PrimaryButton><Check style={{ width: 16, height: 16 }} />Accept all 6</PrimaryButton>
          <Button onClick={() => setFocusMode(true)}><Keyboard style={{ width: 16, height: 16 }} />Focus mode</Button>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <PairCard confidence="Very likely" position="1 / 14" leftTitle="Dhaniya Powder" leftMeta="NEW in this book · 18 dishes · Spice · grams" rightTitle="Coriander Powder" rightMeta="EXISTING in library · used in 6 books · Spice · grams" evidence={['Known synonym — dhaniya = coriander', 'Never used together in any dish, ever', 'Same category, same unit']} actions={['Same — keep “Coriander Powder”', 'Same — rename to “Dhaniya Powder”', 'Different — keep both']} onDecision={onDecision} />
        <PairCard confidence="Very likely" position="2 / 14" flag="Both new here" leftTitle="Curd" leftMeta="NEW · 30 dishes" rightTitle="Dahi" rightMeta="NEW · 4 dishes" evidence={['Known synonym', 'Never used together']} actions={['Same — keep “Curd”', 'Same — keep “Dahi”', 'Different — keep both']} onDecision={onDecision} />
      </div>
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', alignItems: 'center', gap: 12, borderRadius: 8, border: '1px solid #e2e8f0', backgroundColor: '#f8fafc', padding: '12px 16px' }}>
        <p style={{ margin: 0, minWidth: 0, fontSize: 12, color: 'rgba(30, 41, 59, 0.55)' }}><strong style={{ color: '#1e293b' }}>388 other ingredients</strong> were added automatically. No action needed.</p>
        <Button onClick={() => openDrawer('automatic-ingredients')} style={{ minHeight: 32, backgroundColor: '#ffffff', padding: '0 8px', fontSize: 12 }}>View list</Button>
      </div>
    </div>
  );
}

function PreparationsStage({ onDecision }) {
  return (
    <div style={{ maxWidth: '56rem', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }} className="rlr-animate-stage">
      <PairCard confidence="Likely" position="1 / 5" leftTitle="White Sauce (new)" leftMeta="Used by 4 dishes" rightTitle="White Sauce (library)" rightMeta="Used in 3 books" recipes={{ left: ['Butter     60 g', 'Flour      60 g', 'Milk       900 ml', 'Salt          5 g'], right: ['Butter     60 g', 'Flour      58 g', 'Milk       905 ml', 'Salt          5 g', 'Pepper      2 g  ← only here'] }} evidence={['Main ingredients match', 'Only a seasoning differs', 'Quantities vary by less than 2%']} actions={['Same preparation', 'Different — make it a variant', 'Completely different']} onDecision={onDecision} />
      <PairCard confidence="Likely" position="2 / 5" leftTitle="Green Chutney" leftMeta="NEW · Mint base" rightTitle="Mint Chutney" rightMeta="EXISTING · Mint base" evidence={['Six of seven ingredients match', 'Used with the same dishes', 'Only the name is different']} actions={['Same — keep “Mint Chutney”', 'Make Green Chutney a variant', 'Completely different']} onDecision={onDecision} />
    </div>
  );
}

function DishesStage({ onDecision }) {
  return (
    <div style={{ maxWidth: '56rem', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }} className="rlr-animate-stage">
      <PairCard confidence="Very likely" position="1 / 9" leftTitle="Capuccino" leftMeta="NEW · ₹150 · Hot Coffee" rightTitle="Cappuccino" rightMeta="EXISTING · in 8 books" evidence={['One letter different', 'Same category, same price range']} actions={['Same — keep “Cappuccino”', 'Same — rename to “Capuccino”', 'Different dishes']} onDecision={onDecision} />
      <DecisionCard title="Cold Coffee / Cold Coffee Large" badge="Sizes detected" description="These names differ only by size. Keep them as one dish with two sizes?" actions={['These are sizes', 'Keep separate dishes', 'Drop large']} onDecision={onDecision} />
      <DecisionCard title="Tea / Coffee" badge="Two names" description="This menu line appears to contain two different dishes." actions={['Split into two dishes', 'Keep as one dish', 'Drop']} warning onDecision={onDecision} />
    </div>
  );
}

function StructureStage({ onDecision }) {
  return (
    <div style={{ maxWidth: '48rem', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 12 }} className="rlr-animate-stage">
      <DecisionCard title="White Sauce has no recipe in this book" badge="Blocks sealing" description={<>Used by Alfredo Pasta, Pasta Bake, and Lasagna.</>} actions={['Add recipe', 'Expand into raw ingredients', 'Drop these 3 dishes']} danger onDecision={onDecision} />
      <DecisionCard title="Tea / Coffee is two dishes on one line" badge="Needs answer" description="Split the line so each dish can have its own price and recipe." actions={['Split into two', 'Keep as one', 'Drop']} warning onDecision={onDecision} />
      <DecisionCard title="Masala Base has no recorded yield" badge="Needs answer" description="The preparation is used in six dishes, but its cooked quantity is missing." actions={['Set yield to 720 g', 'Enter another yield', 'Keep warning']} warning onDecision={onDecision} />
    </div>
  );
}

function SealStage({ openDrawer, canSeal }) {
  const [sealed, setSealed] = useState(false);
  if (sealed) {
    return (
      <div style={{ maxWidth: '36rem', margin: '0 auto', padding: '2rem 0', textAlign: 'center' }} className="rlr-animate-stage">
        <span style={{ margin: '0 auto', display: 'grid', width: 56, height: 56, placeItems: 'center', borderRadius: '50%', backgroundColor: '#057a55', color: '#fff' }}>
          <Check style={{ width: 28, height: 28 }} strokeWidth={3} />
        </span>
        <h2 style={{ marginTop: 20, fontFamily: 'Sora, sans-serif', fontSize: 28, fontWeight: 700, color: '#1e293b' }}>Bean There is sealed</h2>
        <p style={{ marginTop: 8, fontSize: 14, lineHeight: '1.5rem', color: 'rgba(30, 41, 59, 0.55)' }}>Version 1 is now permanent and read-only in the recipe library.</p>
      </div>
    );
  }
  return (
    <div style={{ maxWidth: '42rem', margin: '0 auto' }} className="rlr-animate-stage">
      <div style={{ textAlign: 'center' }}>
        <span style={{ margin: '0 auto', display: 'grid', width: 48, height: 48, placeItems: 'center', borderRadius: '50%', backgroundColor: '#e6f6f0', color: '#057a55' }}>
          <CheckCircle2 style={{ width: 24, height: 24 }} />
        </span>
        <h2 style={{ marginTop: 16, fontFamily: 'Sora, sans-serif', fontSize: 28, fontWeight: 700, color: '#1e293b' }}>Bean There is ready</h2>
        <p style={{ marginTop: 8, fontSize: 14, color: 'rgba(30, 41, 59, 0.55)' }}>215 dishes · 12 preparations · 340 ingredients</p>
      </div>
      <div style={{ marginTop: 32, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', padding: '24px 0' }}>
        <div>
          <h3 style={{ margin: 0, fontFamily: 'Sora, sans-serif', fontSize: 14, fontWeight: 700, color: '#1e293b' }}>You made 31 decisions</h3>
          <dl style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14, color: 'rgba(30, 41, 59, 0.6)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}><dt>Merged into existing</dt><dd style={{ margin: 0, fontWeight: 700, color: '#1e293b' }}>18</dd></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}><dt>Kept separate</dt><dd style={{ margin: 0, fontWeight: 700, color: '#1e293b' }}>9</dd></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}><dt>Units set</dt><dd style={{ margin: 0, fontWeight: 700, color: '#1e293b' }}>4</dd></div>
          </dl>
        </div>
        <div>
          <h3 style={{ margin: 0, fontFamily: 'Sora, sans-serif', fontSize: 14, fontWeight: 700, color: '#1e293b' }}>Added to the library</h3>
          <dl style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14, color: 'rgba(30, 41, 59, 0.6)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}><dt>New ingredients</dt><dd style={{ margin: 0, fontWeight: 700, color: '#1e293b' }}>12</dd></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}><dt>New dishes</dt><dd style={{ margin: 0, fontWeight: 700, color: '#1e293b' }}>3</dd></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}><dt>New aliases</dt><dd style={{ margin: 0, fontWeight: 700, color: '#057a55' }}>31</dd></div>
          </dl>
        </div>
      </div>
      <button type="button" onClick={() => openDrawer('warnings')} style={{ marginTop: 20, display: 'grid', gridTemplateColumns: 'auto minmax(0,1fr) auto', alignItems: 'center', gap: 12, width: '100%', borderRadius: 8, border: '1px solid rgba(194,120,3,0.3)', backgroundColor: '#fef7e6', padding: 16, textAlign: 'left', cursor: 'pointer' }}>
        <CircleAlert style={{ width: 20, height: 20, color: '#c27803' }} />
        <span style={{ minWidth: 0, fontSize: 14, fontWeight: 600, color: '#1e293b' }}>3 warnings — read before sealing</span>
        <ChevronRight style={{ width: 16, height: 16, color: 'rgba(30, 41, 59, 0.4)' }} />
      </button>
      <p style={{ marginTop: 32, textAlign: 'center', fontSize: 12, color: 'rgba(30, 41, 59, 0.45)' }}>Sealing makes this book permanent and read-only.</p>
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
        <PrimaryButton disabled={!canSeal} onClick={() => setSealed(true)} style={{ minWidth: 160 }}>
          <LockKeyhole style={{ width: 16, height: 16 }} />Seal book
        </PrimaryButton>
      </div>
    </div>
  );
}

function Drawer({ kind, onClose }) {
  if (!kind) return null;
  const data = {
    'clean-list': { title: 'All cleaned rows', subtitle: '359 automatic changes', rows: ['TATA Salt 1kg → Salt', 'Sugar 0.5 Kg → Sugar 500 g', 'Eggs 1 Dozen → Egg 12 piece', 'Amul Taaza → Milk', 'Dhaniya leaves → Coriander leaves'] },
    'automatic-ingredients': { title: 'Added automatically', subtitle: '388 confident ingredients', rows: ['Arabica coffee beans', 'Belgian chocolate', 'Brown bread', 'Fresh basil', 'Vanilla pod'] },
    warnings: { title: 'Warnings before sealing', subtitle: 'These do not block sealing', rows: ['Saffron is listed as 2,000 g in one dish', 'Masala Base loses 28% while cooking', 'Three dishes have no selling price'] },
  }[kind];
  return (
    <div className="rlr-drawer-bg" role="dialog" aria-modal="true" aria-label={data.title} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <aside className="rlr-drawer-pane rlr-animate-drawer">
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', alignItems: 'flex-start', gap: 16 }}>
          <div>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: '#057a55' }}>Details</p>
            <h2 style={{ margin: '4px 0 0 0', fontFamily: 'Sora, sans-serif', fontSize: 20, fontWeight: 700, color: '#1e293b' }}>{data.title}</h2>
            <p style={{ margin: '4px 0 0 0', fontSize: 14, color: 'rgba(30, 41, 59, 0.5)' }}>{data.subtitle}</p>
          </div>
          <button type="button" onClick={onClose} style={{ borderRadius: 6, border: '1px solid #e2e8f0', padding: 8, background: 'transparent', cursor: 'pointer', color: 'rgba(30, 41, 59, 0.55)' }} aria-label="Close panel">
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>
        <div style={{ marginTop: 24, borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
          {data.rows.map((row, index) => (
            <div key={row} style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0,1fr)', gap: 12, padding: '16px 0', borderBottom: index < data.rows.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
              <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 12, fontWeight: 700, color: 'rgba(30, 41, 59, 0.3)' }}>{String(index + 1).padStart(2, '0')}</span>
              <p style={{ margin: 0, fontSize: 14, lineHeight: '1.5rem', color: 'rgba(30, 41, 59, 0.7)' }}>{row}</p>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}

export default function RecipeLibraryReview({ goTo: navigateTo, selectedBook }) {
  const [current, setCurrent] = useState(0);
  const [completed, setCompleted] = useState(new Set());
  const [highestVisited, setHighestVisited] = useState(0);
  const [savedText, setSavedText] = useState('All changes saved');
  const [drawer, setDrawer] = useState(null);

  const stage = STAGES[current];
  const copy = STAGE_COPY[stage.key];
  const canContinue = current < STAGES.length - 1;

  function markSaved() {
    setSavedText('Saving…');
    window.setTimeout(() => setSavedText('Saved just now'), 450);
  }

  function goTo(index) {
    setCurrent(index);
    setHighestVisited((value) => Math.max(value, index));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function continueStage() {
    if (!canContinue) return;
    setCompleted((value) => new Set([...value, current]));
    goTo(current + 1);
    markSaved();
  }

  useEffect(() => {
    function onKeyDown(event) {
      if (stage.key !== 'ingredients') return;
      if (event.key.toLowerCase() === 's' || event.key.toLowerCase() === 'd') markSaved();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [stage.key]);

  return (
    <main className="rlr-page">
      <div className="rlr-card-shell">
        <header className="rlr-header">
          <div className="rlr-header-grid">
            <div style={{ minWidth: 0 }}>
              <p className="rlr-breadcrumb">
                <span 
                  style={{ cursor: 'pointer', transition: 'color 0.2s' }}
                  onClick={() => navigateTo && navigateTo('recipe-library')}
                  onMouseEnter={(e) => (e.target.style.color = '#059669')}
                  onMouseLeave={(e) => (e.target.style.color = 'inherit')}
                >
                  Recipe Library
                </span>{' '}
                <span className="rlr-breadcrumb-sep">›</span>{' '}
                <span 
                  style={{ cursor: 'pointer', transition: 'color 0.2s' }}
                  onClick={() => navigateTo && navigateTo('recipe-library')}
                  onMouseEnter={(e) => (e.target.style.color = '#059669')}
                  onMouseLeave={(e) => (e.target.style.color = 'inherit')}
                >
                  Books
                </span>{' '}
                <span className="rlr-breadcrumb-sep">›</span> {selectedBook?.title || 'Bean There'}
              </p>
              <div className="rlr-header-title-box">
                <h1 className="rlr-h1">{selectedBook?.title || 'Bean There'}</h1>
                <p className="rlr-meta-sub">
                  {selectedBook?.subtitle ? `${selectedBook.vol} · ${selectedBook.location}` : 'BID 67 · Cafe · v1'}
                </p>
              </div>
            </div>
            <div className="rlr-saved-badge">
              <span>{savedText}</span>
              <Check style={{ width: 14, height: 14 }} />
            </div>
          </div>
        </header>

        <StageStepper current={current} completed={completed} onSelect={goTo} />

        <section className="rlr-main-sec" aria-labelledby="stage-title">
          <div className="rlr-stage-head">
            <p className="rlr-eyebrow">{copy.eyebrow}</p>
            <h2 id="stage-title" className="rlr-h2">{copy.title}</h2>
            <p className="rlr-stage-desc">{copy.description}</p>
          </div>

          {stage.key === 'clean' && <CleanStage openDrawer={setDrawer} />}
          {stage.key === 'units' && <UnitsStage onDecision={markSaved} />}
          {stage.key === 'ingredients' && <IngredientsStage reopenWarning={highestVisited >= 4} openDrawer={setDrawer} onDecision={markSaved} />}
          {stage.key === 'preparations' && <PreparationsStage onDecision={markSaved} />}
          {stage.key === 'dishes' && <DishesStage onDecision={markSaved} />}
          {stage.key === 'structure' && <StructureStage onDecision={markSaved} />}
          {stage.key === 'seal' && <SealStage openDrawer={setDrawer} canSeal={completed.has(5)} />}
        </section>

        <footer className="rlr-footer-bar">
          <div className="rlr-footer-grid">
            <p className="rlr-footer-count">
              {stage.count > 0 ? `${stage.count} ${stage.count === 1 ? 'item' : 'items'} left in this stage` : current === 0 ? 'Read-only summary' : 'Final review'}
            </p>
            {current > 0 && <Button onClick={() => goTo(current - 1)}><ArrowLeft style={{ width: 16, height: 16 }} />Back</Button>}
            {canContinue && <PrimaryButton onClick={continueStage}>Continue <ArrowRight style={{ width: 16, height: 16 }} /></PrimaryButton>}
            {!canContinue && current > 0 && <Button onClick={() => goTo(current - 1)}><ArrowLeft style={{ width: 16, height: 16 }} />Back</Button>}
          </div>
        </footer>
      </div>
      <Drawer kind={drawer} onClose={() => setDrawer(null)} />
    </main>
  );
}
